import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

const scrollPositions = new Map<string, number>();

export const clearScrollPositions = () => scrollPositions.clear();

interface UseListScrollRestorationProps {
  scrollKey?: string;
  hasItems: boolean;
  externalRef?: RefObject<HTMLDivElement | null>;
}

export function useListScrollRestoration({
  scrollKey,
  hasItems,
  externalRef,
}: UseListScrollRestorationProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const restoredKeyRef = useRef<string | null>(null);

  const setContainer = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node;
      if (externalRef) {
        externalRef.current = node;
      }
    },
    [externalRef],
  );

  useEffect(() => {
    const node = nodeRef.current;
    if (!node || !scrollKey) {
      return;
    }
    const handleScroll = () => scrollPositions.set(scrollKey, node.scrollTop);
    node.addEventListener('scroll', handleScroll, { passive: true });
    return () => node.removeEventListener('scroll', handleScroll);
  }, [scrollKey]);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (
      !node ||
      !scrollKey ||
      !hasItems ||
      restoredKeyRef.current === scrollKey
    ) {
      return;
    }
    restoredKeyRef.current = scrollKey;
    const saved = scrollPositions.get(scrollKey);
    if (saved) {
      node.scrollTop = saved;
    }
  }, [scrollKey, hasItems]);

  return { setContainer };
}
